"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE PASSWORD POLICY, ONCE. GoTrue enforces it; this is what we RENDER.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The account-settings screen draws a live checklist as somebody types — *"at least N
 * characters"*, *"contains a letter and a digit"* — and that checklist has to agree with whatever
 * actually refuses the password. Two opinions here is not a cosmetic problem: a green tick beside
 * a rule the server then rejects makes the form look broken rather than the password wrong.
 *
 * ⚠️ **THE MOCKUP SAYS TEN AND OUR CONFIGURATION SAYS TWELVE.** `main.jsx`'s `usPwChecks()` is
 * `p.next.length >= 10`; `supabase/config.toml` sets `minimum_password_length = 12` with
 * `password_requirements = "lower_upper_letters_digits"`, and a comment there explaining that
 * Supabase's own floor of 6 was raised deliberately. The mockup is a prototype with a mock
 * service that accepts anything, so it never found out. **The configuration wins**, and this file
 * is why the screen knows that.
 *
 * ⚠️ **`auth.controller.ts` still does NOT validate length on the way in, and that stays true.**
 * Its note: *"the POLICY lives in `config.toml` and GoTrue enforces it, returning the reasons —
 * duplicating a length here would be a second opinion that drifts."* This constant is for
 * **rendering**, not for refusing. The server's answer is still GoTrue's.
 *
 * ⚠️ Kept honest by `__tests__/password-policy.test.ts`, which READS `config.toml` and fails when
 * the two disagree. A constant that claims to mirror a config file and is never compared to it is
 * a third opinion with better manners.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PASSWORD_RULES = exports.PASSWORD_MIN_LENGTH = void 0;
exports.checkPassword = checkPassword;
exports.passwordAcceptable = passwordAcceptable;
/** Mirrors `supabase/config.toml`. Changing one without the other fails a test. */
exports.PASSWORD_MIN_LENGTH = 12;
/**
 * `lower_upper_letters_digits` in GoTrue's vocabulary means: at least one lower-case letter, one
 * upper-case letter and one digit.
 *
 * ⚠️ **Not a regex, and not one boolean.** The screen shows a *list* and ticks the rules
 * individually as they are satisfied — one combined predicate would render as a single line that
 * says "no" without saying which part.
 */
exports.PASSWORD_RULES = [
    { id: 'length', min: exports.PASSWORD_MIN_LENGTH },
    { id: 'letters' },
    { id: 'digits' },
    { id: 'match' },
];
/**
 * Which rules a candidate satisfies.
 *
 * ⚠️ **PURE, and in `shared`, for the reason `effective.ts` gives about duplicated predicates:**
 * *"the copy that drifts is the one deciding what a UI offers while the server quietly
 * disagrees."* Here the server is GoTrue, so drift is guaranteed unless there is exactly one
 * implementation of what we *believe* the policy is.
 */
function checkPassword(next, confirmation) {
    return {
        length: next.length >= exports.PASSWORD_MIN_LENGTH,
        /* Both cases, because `lower_upper_letters_digits` requires both. A check for `/[a-zA-Z]/` —
         * which is what the mockup does — passes for an all-lower-case password that GoTrue refuses. */
        letters: /\p{Ll}/u.test(next) && /\p{Lu}/u.test(next),
        digits: /\p{Nd}/u.test(next),
        /* ⚠️ An empty pair is NOT a match. Without the first clause two blank fields satisfy this rule
         * and the checklist shows a tick before anybody has typed. */
        match: next.length > 0 && next === confirmation,
    };
}
/** Whether every rule passes. The submit button's enabled state, and nothing more. */
function passwordAcceptable(next, confirmation) {
    return Object.values(checkPassword(next, confirmation)).every(Boolean);
}
//# sourceMappingURL=password-policy.js.map